import { SSE_LAST_EVENT_ID_HEADER, isSseEventName } from "@sagot/constants";
import { readChatEvents } from "./chat-events";
import {
  HTTP_TOO_MANY_REQUESTS,
  POLL_INTERVAL_MS,
  RETRY_AFTER_HEADER,
  SSE_CONTENT_TYPE,
  STREAM_FIRST_BYTE_TIMEOUT_MS,
  STREAM_RETRY_DELAYS_MS,
} from "./constants";
import { decodeChatEvent } from "./decode";
import type { ChatEvent, ErrorPayload, StreamStatus } from "./types";

export type HeadersSource = HeadersInit | (() => HeadersInit);

export interface PollContext {
  requestId: string;
  lastEventId: string | null;
}

export interface PollOptions {
  /** GET URL returning `{ events: [{ id, event, data }] }` after `lastEventId`. */
  url: (ctx: PollContext) => string;
  intervalMs?: number;
}

export interface StreamClientOptions {
  endpoint: string;
  headers?: HeadersSource;
  fetchImpl?: typeof fetch;
  poll?: PollOptions;
  firstByteTimeoutMs?: number;
  retryDelaysMs?: readonly number[];
}

export interface StreamRequest {
  body: unknown;
  /** Idempotency key for the run: resume and polling address the same server run. */
  requestId: string;
}

export interface StreamHandlers {
  onEvent: (event: ChatEvent) => void;
  onStatus: (status: StreamStatus) => void;
}

export type StreamResult =
  | { outcome: "done" }
  | { outcome: "aborted" }
  | { outcome: "error"; error: ErrorPayload; fromEvent: boolean; receivedAny: boolean };

class RetryableError extends Error {}

interface RunContext {
  options: StreamClientOptions;
  request: StreamRequest;
  handlers: StreamHandlers;
  signal: AbortSignal;
  lastEventId: string | null;
  receivedAny: boolean;
}

export function resolveHeaders(source: HeadersSource | undefined): Headers {
  return new Headers(typeof source === "function" ? source() : source);
}

/** Reads the live flag (a call, so TS does not narrow it across awaits). */
export function isAborted(signal: AbortSignal): boolean {
  return signal.aborted;
}

export function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    }
    signal.addEventListener("abort", done, { once: true });
  });
}

/** Wraps a body so that silence before the first byte closes it and flags a stall. */
export function watchFirstByte(source: ReadableStream<Uint8Array>, timeoutMs: number, signal?: AbortSignal) {
  const reader = source.getReader();
  let stalled = false;
  let finished = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stream = new ReadableStream<Uint8Array>({
    start(controller) {
      timer = setTimeout(() => {
        stalled = true;
        finished = true;
        controller.close();
        void reader.cancel().catch(() => undefined);
      }, timeoutMs);
      // Abort ends the read even when the transport does not (mocks, some polyfills).
      signal?.addEventListener(
        "abort",
        () => {
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          controller.error(new DOMException("Aborted", "AbortError"));
          void reader.cancel().catch(() => undefined);
        },
        { once: true },
      );
    },
    async pull(controller) {
      const { value, done } = await reader.read();
      if (finished) return;
      clearTimeout(timer);
      if (done) {
        finished = true;
        controller.close();
      } else controller.enqueue(value);
    },
    cancel(reason) {
      finished = true;
      clearTimeout(timer);
      return reader.cancel(reason);
    },
  });
  return { stream, isStalled: () => stalled };
}

function retryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers.get(RETRY_AFTER_HEADER);
  const seconds = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(seconds) ? seconds : undefined;
}

/** Maps a non-OK response to a terminal error, or throws RetryableError for 5xx. */
function httpError(response: Response): ErrorPayload {
  if (response.status >= 500) throw new RetryableError(`HTTP ${response.status}`);
  if (response.status === HTTP_TOO_MANY_REQUESTS) return { code: "rate_limited", message: "", retry_after: retryAfterSeconds(response) };
  return { code: `http_${response.status}`, message: "" };
}

function deliver(ctx: RunContext, event: ChatEvent): StreamResult | null {
  ctx.receivedAny = true;
  if (event.id !== null) ctx.lastEventId = event.id;
  ctx.handlers.onEvent(event);
  if (event.type === "done") return { outcome: "done" };
  if (event.type === "error") return { outcome: "error", error: event.data, fromEvent: true, receivedAny: true };
  return null;
}

/** `"poll"`: switch to (or keep) polling — streaming is blocked or the run is still pending. */
type AttemptResult = StreamResult | "poll";

async function streamAttempt(ctx: RunContext): Promise<AttemptResult> {
  const { options, request, signal } = ctx;
  const headers = resolveHeaders(options.headers);
  headers.set("Accept", SSE_CONTENT_TYPE);
  headers.set("Content-Type", "application/json");
  if (ctx.lastEventId !== null) headers.set(SSE_LAST_EVENT_ID_HEADER, ctx.lastEventId);
  const fetchImpl = options.fetchImpl ?? fetch;
  const response = await fetchImpl(options.endpoint, { method: "POST", headers, body: JSON.stringify(request.body), signal });
  if (!response.ok) return { outcome: "error", error: httpError(response), fromEvent: false, receivedAny: ctx.receivedAny };
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes(SSE_CONTENT_TYPE) || !response.body) return "poll";
  const watched = watchFirstByte(response.body, options.firstByteTimeoutMs ?? STREAM_FIRST_BYTE_TIMEOUT_MS, signal);
  for await (const raw of readChatEvents(watched.stream, { lastEventId: ctx.lastEventId, signal })) {
    const event = decodeChatEvent(raw);
    if (!event) continue;
    const result = deliver(ctx, event);
    if (result) return result;
  }
  if (signal.aborted) return { outcome: "aborted" };
  if (watched.isStalled() && !ctx.receivedAny) return "poll";
  throw new RetryableError("stream closed before done");
}

async function pollOnce(ctx: RunContext, poll: PollOptions): Promise<StreamResult | null> {
  const headers = resolveHeaders(ctx.options.headers);
  headers.set("Accept", "application/json");
  const fetchImpl = ctx.options.fetchImpl ?? fetch;
  const response = await fetchImpl(poll.url({ requestId: ctx.request.requestId, lastEventId: ctx.lastEventId }), { headers, signal: ctx.signal });
  if (!response.ok) return { outcome: "error", error: httpError(response), fromEvent: false, receivedAny: ctx.receivedAny };
  const json = (await response.json()) as { events?: { id?: string | null; event: string; data: unknown }[] };
  for (const raw of json.events ?? []) {
    if (!isSseEventName(raw.event)) continue;
    const event = decodeChatEvent({ type: raw.event, data: raw.data, id: raw.id ?? null });
    if (!event) continue;
    const result = deliver(ctx, event);
    if (result) return result;
  }
  return null;
}

async function withRetries(ctx: RunContext, attempt: () => Promise<AttemptResult>): Promise<AttemptResult> {
  const delays = ctx.options.retryDelaysMs ?? STREAM_RETRY_DELAYS_MS;
  let failures = 0;
  for (;;) {
    const progressBefore = ctx.lastEventId;
    try {
      return await attempt();
    } catch (error) {
      if (ctx.signal.aborted) return { outcome: "aborted" };
      if (!(error instanceof RetryableError) && !(error instanceof TypeError)) throw error;
      if (ctx.lastEventId !== progressBefore) failures = 0;
      const delay = delays[failures];
      if (delay === undefined) return { outcome: "error", error: { code: "network", message: "" }, fromEvent: false, receivedAny: ctx.receivedAny };
      failures += 1;
      ctx.handlers.onStatus("reconnecting");
      await sleep(delay, ctx.signal);
      if (isAborted(ctx.signal)) return { outcome: "aborted" };
    }
  }
}

async function pollLoop(ctx: RunContext): Promise<StreamResult> {
  const poll = ctx.options.poll;
  if (!poll) return { outcome: "error", error: { code: "stream_unsupported", message: "" }, fromEvent: false, receivedAny: ctx.receivedAny };
  ctx.handlers.onStatus("polling");
  for (;;) {
    const result = await withRetries(ctx, async () => (await pollOnce(ctx, poll)) ?? "poll");
    if (result !== "poll") return result;
    await sleep(poll.intervalMs ?? POLL_INTERVAL_MS, ctx.signal);
    if (ctx.signal.aborted) return { outcome: "aborted" };
  }
}

/**
 * Runs one chat request: SSE-over-fetch → resume with Last-Event-ID on disconnect (backoff) →
 * polling fallback when streaming is blocked (non-SSE response or no bytes in time).
 */
export async function runChatStream(options: StreamClientOptions, request: StreamRequest, handlers: StreamHandlers, signal: AbortSignal): Promise<StreamResult> {
  const ctx: RunContext = { options, request, handlers, signal, lastEventId: null, receivedAny: false };
  try {
    const result = await withRetries(ctx, () => streamAttempt(ctx));
    return result === "poll" ? await pollLoop(ctx) : result;
  } catch (error) {
    if (signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return { outcome: "aborted" };
    return { outcome: "error", error: { code: "unknown", message: "" }, fromEvent: false, receivedAny: ctx.receivedAny };
  }
}
